import React, { useState, useCallback, useEffect } from 'react';
import fetchEndpoint from '../../../services/fetchEndpoint';
import { sortChartData, getYAxisValues, setLineXMargins } from '../Charts/Line/LineHelpers';

import { userAppContext, searchContext } from '../../../context/Context';
import buildGraphEndpoint from '../../../utils/buildGraphEndpoint';

function useLineChart({
  chartInfo,
  initialChartData,
  keySearch,
  source,
  baseUrl,
  parent,
  view,
  getLineData,
  width,
  uniqueIdentViewsVariables,
  pageSize,
}) {
  const fetchAll = pageSize === -1;
  const effectivePageSize = fetchAll ? 100 : pageSize;
  const [chartData, setChartData] = useState(initialChartData);
  const [yAxisValue, setYAxisValues] = useState({ max: 1, min: 0 });
  const [isPayloadEmpty, setIsPayloadEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [xMargin, setXMargin] = useState(null);
  const [totalResults, setTotalResults] = useState(0);

  const { authorization, setIsAuthenticated, setGroups, setAuthError, setAuthorization } =
    userAppContext() || {};

  const { tab, searchItems } = searchContext() || { tab: null, searchItems: [] };

  const setLineChartData = useCallback(async () => {
    setIsLoading(true);

    const orderByParam =
      chartInfo.variable === 'numPMIDsupportingAssociation'
        ? `${chartInfo.variable}_${source}`
        : chartInfo.variable;

    const order = 'desc';
    const pageNum = 0;
    const firstUrl = buildGraphEndpoint(
      view,
      source,
      tab,
      searchItems,
      pageNum,
      effectivePageSize,
      orderByParam,
      order,
    );
    const firstResponse = await fetchEndpoint(firstUrl, authorization, setIsAuthenticated);
    if (firstResponse === undefined) {
      setIsLoading(false);
      return;
    }

    const firstData = firstResponse.payload || [];
    const totalElements = firstResponse?.paging?.totalElements ?? firstData.length ?? 0;
    setTotalResults(totalElements);

    if (firstData.length === 0) {
      setIsPayloadEmpty(true);
      setIsLoading(false);
      return;
    }
    setIsPayloadEmpty(false);

    let completeData = [...firstData];
    if (fetchAll) {
      const totalPages = Math.ceil(totalElements / effectivePageSize);
      for (let currentPage = 1; currentPage < totalPages; currentPage += 1) {
        const pagedUrl = buildGraphEndpoint(
          view,
          source,
          tab,
          searchItems,
          currentPage,
          effectivePageSize,
          orderByParam,
          order,
        );
        const pagedResponse = await fetchEndpoint(pagedUrl, authorization, setIsAuthenticated);
        if (pagedResponse?.payload?.length) {
          completeData = [...completeData, ...pagedResponse.payload];
        }
      }
    }

    const mappingVariables = uniqueIdentViewsVariables[parent].mappingIdents[view];
    const model = uniqueIdentViewsVariables[parent].model;
    const transformedLineData = getLineData(completeData, mappingVariables, model, chartInfo.variable);

    if (transformedLineData.length === 0) {
      setChartData([]);
      setIsLoading(false);
      return;
    }

    const newYAxisValues = getYAxisValues(chartInfo.variable, completeData);
    setYAxisValues(newYAxisValues);

    const renderedResults = transformedLineData[0].data.length;
    setXMargin(() => setLineXMargins(width, renderedResults));
    sortChartData(transformedLineData);
    setChartData(transformedLineData);
    setIsLoading(false);
  }, [source, tab, baseUrl, keySearch, view, parent, chartInfo, pageSize, fetchAll, effectivePageSize]);

  useEffect(() => {
    setLineChartData();
  }, [setLineChartData]);

  return {
    chartData,
    yAxisValue,
    isPayloadEmpty,
    isLoading,
    xMargin,
    setXMargin,
    totalResults,
  };
}

export default useLineChart;
